import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  Button,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle as _DialogTitle,
  FormControl,
  FormHelperText as _FormHelperText,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import { createElement, FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import CreateModificationRequest from '../../../../entity/CreateModificationRequest';
import UploadingFile from '../../../../entity/UploadingFile';
import UploadingFilesProgress from '../../../../entity/UploadingFilesProgress';
import { DeliverableForCraetionDetailFragment } from '../../../../generated/graphql';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import useInputDuration from '../../../../hooks/useInputDuration';
import theme from '../../../../theme';
import hmsToSeconds from '../../../../utility/hmsToSeconds';
import secondsToHms from '../../../../utility/secondsToHms';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import ImageScreen from '../../primitive/ImageScreen';
import FileList, { FileListItem, FileListUploadingItem } from '../FileList';
import _FileUploader from '../FileUploader';
import PreviewVideoScreen from '../PreviewVideoScreen';

interface Props {
  createModificationRequest: CreateModificationRequest;
  deliverable: DeliverableForCraetionDetailFragment;
  files: Array<FileReference>;
  uploadingAssetFiles: Array<UploadingFile>;
  uploadingFilesProgress: UploadingFilesProgress;
  bucketId: string | null;
  isOpen: boolean;
  onRequestUploadFiles: (files: UploadingFile[]) => void;
  onRemoveAssetFile: (bucketId: string, fileId: string) => void;
  onRequestClose: (_: any) => void;
  onSubmit: (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => Promise<void>;
  className?: string;
}

const CreatingModificationRequestDialog: FC<Props> = ({
  createModificationRequest,
  deliverable,
  files,
  uploadingAssetFiles,
  uploadingFilesProgress,
  bucketId,
  isOpen,
  onRequestUploadFiles,
  onRemoveAssetFile,
  onRequestClose,
  onSubmit,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [inputComment, commentHandler] = useInputComment('');
  const [inputDuration, durationHandler] = useInputDuration('00:00:00', videoRef);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const uploadedFiles = files.filter((file) => createModificationRequest.assetIds.includes(file.id));

  useEffect(() => {
    // * Using setTimeout for reference ref object.
    setTimeout(() => {
      durationHandler.setVideoRef(videoRef);
    }, 0);
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog className={className} open={isOpen} maxWidth="lg">
      <DialogTitle disableTypography>
        <Typography variant="h6">
          <FormattedMessage id="modification_request.creating_dialog.title" />
        </Typography>
      </DialogTitle>

      <DialogContent>
        <DeliverableScreen>
          {(deliverable.creative.file.mimeType.startsWith('video/') ||
            deliverable.creative.file.mimeType.startsWith('audio/')) && (
            <VideoOrImage aspectRatio={[4, 3]}>
              <PreviewVideoScreen
                src={deliverable.creative.file.url}
                videoRef={videoRef}
                onSeek={(time: number) => {
                  const formattedTime = secondsToHms(time);
                  durationHandler.setDuration(formattedTime);
                }}
                onTimeUpdate={(time: number) => {
                  const formattedTime = secondsToHms(time);
                  durationHandler.setDuration(formattedTime);
                }}
                controls
                autoPlay
                nodownload
              />
            </VideoOrImage>
          )}
          {deliverable.creative.file.mimeType.startsWith('image/') && (
            <VideoOrImage aspectRatio={[4, 3]}>
              <ImageScreen src={deliverable.creative.file.url!} />
            </VideoOrImage>
          )}
        </DeliverableScreen>

        <Form>
          {deliverable.creative.file.mimeType.startsWith('video/') ||
          deliverable.creative.file.mimeType.startsWith('audio/') ? (
            <Durations>
              <FormControl>
                <InputDuration
                  label={<FormattedMessage id="modification_request.creating_dialog.input_duration.label" />}
                  value={inputDuration.duration}
                  type="time"
                  variant="outlined"
                  inputProps={{ step: '1' }}
                  error={inputDuration.error}
                  onChange={(e) => {
                    durationHandler.setDuration(e.target.value);
                    videoRef.current ? (videoRef.current.currentTime = hmsToSeconds(e.target.value)) : null;
                  }}
                  onFocus={() => {
                    videoRef.current ? videoRef.current.pause() : null;
                  }}
                />
                {inputDuration.error ? (
                  <InputCount>
                    <FormattedMessage id="modification_request.creating_dialog.input_duration.validation_error" />
                  </InputCount>
                ) : null}
              </FormControl>

              {videoRef.current ? (
                <Duration variant="body1" color="textSecondary">
                  {`/ ${secondsToHms(videoRef.current.duration)}`}
                </Duration>
              ) : null}
            </Durations>
          ) : null}

          <CommentForm error={inputComment.error}>
            <Comment
              label={<FormattedMessage id="modification_request.creating_dialog.input_comment.label" />}
              value={inputComment.body}
              variant="outlined"
              minRows="4"
              maxRows="8"
              multiline
              error={inputComment.error}
              onChange={(e) => commentHandler.setComment(e.target.value)}
            />
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>

          <FileList>
            {[
              ...uploadedFiles.map((file) => (
                <FileListItem
                  file={file}
                  key={file.id}
                  isVisibleSecondary={false}
                  onClickDeleteButton={isSubmitted ? undefined : () => onRemoveAssetFile(bucketId!, file.id)}
                />
              )),
              ...uploadingAssetFiles.map((file) => {
                const targetProgressFile = uploadingFilesProgress[file.id];

                return (
                  <FileListUploadingItem
                    file={file}
                    key={`uploading-${file.id}`}
                    progress={targetProgressFile ? targetProgressFile.progress : 0}
                    state="uploading"
                  />
                );
              }),
            ]}
          </FileList>

          {bucketId ? <FileUploader onSelectFiles={(_, files) => onRequestUploadFiles(files)} /> : null}
        </Form>
      </DialogContent>

      <DialogActions>
        <Button
          color="default"
          variant="text"
          onClick={(e) => {
            onRequestClose(e);
            commentHandler.setComment('');
            durationHandler.setDuration('00:00:00');
          }}
          disabled={isSubmitted}
        >
          <FormattedMessage id="modification_request.creating_dialog.cancel" />
        </Button>

        <Button
          variant="text"
          disabled={
            inputComment.body.trim() === '' ||
            inputComment.error ||
            inputDuration.error ||
            isSubmitted ||
            uploadingAssetFiles.length > 0
          }
          onClick={async () => {
            setIsSubmitted(true);
            await onSubmit(
              createModificationRequest.id,
              bucketId,
              inputDuration.duration,
              inputComment.body,
              uploadedFiles,
            );
            commentHandler.setComment('');
            durationHandler.setDuration('00:00:00');
            setIsSubmitted(false);
          }}
        >
          <FormattedMessage id="modification_request.creating_dialog.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogTitle = styled(_DialogTitle)({
  borderBottom: `1px solid ${theme.palette.grey['200']}`,
});

const DialogContent = styled(_DialogContent)(
  gridLayoutContainer({
    columns: ['45%', '55%'],
    rows: ['auto'],
    areas: [['deliverable', 'form']],
  }),
  {
    width: 800,
    paddingTop: SPACING_REGULAR,
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
  marginBottom: SPACING_LARGE,
});

const VideoOrImage = styled(AspectRatioFixed)(gridLayoutItem('video', { alignSelf: 'flex-start' }), {
  marginBottom: SPACING_REGULAR,
});

const Form = styled('div')(gridLayoutItem('form'), {});

const Durations = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }), {
  margin: `0 0 ${SPACING_UNIT * 3}px`,
});

const InputDuration = styled(TextField)({
  minWidth: 175,
});

const Duration = styled(Typography)({
  marginLeft: SPACING_REGULAR,
});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_UNIT,
});

const Comment = styled(TextField)({
  width: '100%',
});

const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});

const FileUploader = styled(_FileUploader)({
  height: 'auto',
});

const DialogActions = styled(_DialogActions)({
  borderTop: `1px solid ${theme.palette.grey['200']}`,
  marginTop: 0,
  paddingTop: SPACING_UNIT * 2,
  paddingBottom: SPACING_UNIT,
  height: 60,
});

export default CreatingModificationRequestDialog;
